import type { ApiError } from '@shared/types';
import { config } from '@shared/config';
import { normalizeError } from './normalizeError';

export interface ApiClient {
  get<T>(url: string, params?: Record<string, string>): Promise<T>;
  /** GET request bypassing the base URL prefix (for non-versioned endpoints like /health). */
  getAbsolute<T>(url: string, params?: Record<string, string>): Promise<T>;
  post<T>(url: string, body?: unknown): Promise<T>;
  put<T>(url: string, body?: unknown): Promise<T>;
  delete<T>(url: string): Promise<T>;
}

export class ApiRequestError extends Error {
  constructor(public readonly apiError: ApiError) {
    super(apiError.message);
    this.name = 'ApiRequestError';
  }
}

export class FetchApiClient implements ApiClient {
  private baseUrl: string;
  private originUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? config.apiBaseUrl;
    try {
      this.originUrl = new URL(this.baseUrl).origin;
    } catch {
      this.originUrl = '';
    }
  }

  async get<T>(url: string, params?: Record<string, string>): Promise<T> {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request<T>(`${url}${query}`, { method: 'GET' });
  }

  async getAbsolute<T>(url: string, params?: Record<string, string>): Promise<T> {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request<T>(`${url}${query}`, { method: 'GET' }, true);
  }

  async post<T>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(url: string): Promise<T> {
    return this.request<T>(url, { method: 'DELETE' });
  }

  private getUserLabel(): string | null {
    try {
      const raw = localStorage.getItem('bv-user-label');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { state?: { entry?: { label?: string } } };
      return parsed?.state?.entry?.label ?? null;
    } catch {
      return null;
    }
  }

  private async request<T>(url: string, init: RequestInit, skipBaseUrl = false): Promise<T> {
    const userLabel = this.getUserLabel();
    const fullUrl = skipBaseUrl ? `${this.originUrl}${url}` : `${this.baseUrl}${url}`;
    const response = await fetch(fullUrl, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(userLabel ? { 'x-user-label': encodeURIComponent(userLabel) } : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      const apiError = await normalizeError(response);
      throw new ApiRequestError(apiError);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }
}

/** Default singleton API client instance. */
export const apiClient: ApiClient = new FetchApiClient();
